/**
 * canvasOps (client) — the optimistic mirror of server/services/canvasOps.js.
 *
 * Every editor gesture becomes ops. They are applied here immediately so the UI
 * responds without waiting for the network, and the same ops are sent to the
 * server, which applies them with identical semantics. From Phase 4, collaborator
 * batches arriving over the socket replay through this same code.
 *
 * The one deliberate difference from the server copy: these appliers are
 * IMMUTABLE. They return fresh nodes/edges arrays (and fresh objects for anything
 * they touched) so React sees a changed reference and re-renders. The server
 * mutates its Mongoose document in place. Everything else — field whitelists,
 * flag reconciliation, choiceType derivation, idempotent creates — must stay in
 * step, and `server/tests/canvasOps.test.js` and
 * `client/src/utils/__tests__/canvasOps.test.js` assert the same behaviours.
 *
 * Validation is intentionally lighter here: the server is authoritative and
 * rejects a bad batch. The client only enforces what changes what the author
 * sees.
 */

import { OBJECT_ID_RE } from './objectId';

/** Fields a client may set through `updateNodeData`. Mirrors NODE_DATA_FIELDS. */
export const NODE_DATA_FIELDS = new Set([
    'title',
    'text',
    'image',
    'audio',
    'audioLabel',
    'document',
    'autoplay',
    'isFreeResponse',
    'isUserSubmitted',
    'isLogged',
    'situationGroup',
    'grantsKeys',
    'removesKeys',
    'endpointType',
    'fillColor',
    'borderColor',
    'sourcePosition',
    'targetPosition',
]);

export const EDGE_DATA_FIELDS = new Set([
    'title',
    'text',
    'image',
    'isMLFRQ',
    'mlfrqPairs',
    'hint',
    'correctness',
    'key',
    'hideIfKey',
    'showIfKey',
    'grantsKeys',
    'removesKeys',
    'fillColor',
    'borderColor',
]);

/** Authorable fields on a draft group — `situations` is derived at publish. */
export const GROUP_DATA_FIELDS = new Set([
    'title',
    'text',
    'color',
    'image',
    'audio',
    'document',
    'audioLabel',
    'autoplay',
]);

export const KEY_DATA_FIELDS = new Set(['name', 'description']);

/**
 * Ops only the scenario's creator may send. The server enforces this (a
 * collaborator's batch containing one is rejected whole, with a 403); the editor
 * uses the same set to avoid offering the gesture in the first place.
 */
export const CREATOR_ONLY_OPS = new Set(['setTableOfContents']);

/**
 * The ReactFlow component keys registered in the editor's `nodeTypes`/`edgeTypes`
 * (components/scenarios/create/Canvas.js). Deliberately constants and NOT read off
 * the op: `op.type` is the op's own discriminator ('addNode', 'addEdge'), so taking
 * the node type from it stamped every created node with the op's name, and
 * ReactFlow fell back to its unstyled built-in renderer — a flat, label-less box.
 * Mirrors NODE_COMPONENT_TYPE/EDGE_COMPONENT_TYPE on the server.
 */
export const NODE_COMPONENT_TYPE = 'situation';
export const EDGE_COMPONENT_TYPE = 'choice';

export class OpError extends Error {
    constructor(message) {
        super(message);
        this.name = 'OpError';
    }
}

const isId = (value) => typeof value === 'string' && OBJECT_ID_RE.test(value);

const pickFields = (patch, allowed) => {
    const clean = {};
    for (const [key, value] of Object.entries(patch || {})) {
        if (allowed.has(key)) clean[key] = value;
    }
    return clean;
};

/**
 * Node-data patch normalisation. Kept in step with the server's
 * `sanitizeNodeData` for the parts an author can observe immediately.
 */
export function sanitizeNodeData(patch = {}, current = {}) {
    const clean = pickFields(patch, NODE_DATA_FIELDS);

    if ('situationGroup' in clean) {
        clean.situationGroup = clean.situationGroup || null;
    }
    for (const key of ['image', 'audio', 'document']) {
        if (key in clean && (clean[key] === '' || clean[key] === 'null')) clean[key] = null;
    }

    // A situation cannot both collect a free response and be user-submitted.
    // Whichever the author just switched on wins.
    const nextFreeResponse = clean.isFreeResponse ?? !!current.isFreeResponse;
    const nextUserSubmitted = clean.isUserSubmitted ?? !!current.isUserSubmitted;
    if (nextFreeResponse && nextUserSubmitted) {
        if (clean.isUserSubmitted) clean.isFreeResponse = false;
        else clean.isUserSubmitted = false;
    }

    return clean;
}

export function sanitizeEdgeData(patch = {}) {
    const clean = pickFields(patch, EDGE_DATA_FIELDS);

    if ('key' in clean) clean.key = clean.key || null;
    if ('image' in clean && (clean.image === '' || clean.image === 'null')) clean.image = null;
    if ('correctness' in clean && !clean.correctness) clean.correctness = null;

    return clean;
}

export function sanitizeGroupData(patch = {}) {
    const clean = pickFields(patch, GROUP_DATA_FIELDS);

    for (const key of ['image', 'audio', 'document']) {
        if (key in clean && (clean[key] === '' || clean[key] === 'null')) clean[key] = null;
    }
    if ('autoplay' in clean) clean.autoplay = !!clean.autoplay;

    return clean;
}

export function sanitizeKeyData(patch = {}) {
    const clean = pickFields(patch, KEY_DATA_FIELDS);
    if ('name' in clean) clean.name = String(clean.name ?? '').trim();
    return clean;
}

/**
 * choiceType is presentation shorthand for two facts that live elsewhere: an edge
 * looping back on its own source IS a hint, and Choice.hasHint derives from the
 * same thing at publish time. Recomputed after anything that could change either.
 */
export function withEdgeType(edge) {
    let choiceType = '';
    if (edge.source && edge.source === edge.target) choiceType = 'hint';
    else if (edge.data?.isMLFRQ) choiceType = 'mlfrq';

    if (edge.data?.choiceType === choiceType) return edge;
    return { ...edge, data: { ...edge.data, choiceType } };
}

const appliers = {
    addNode(graph, op) {
        if (!isId(op.id)) throw new OpError('node id must be a 24-character hex id.');
        if (graph.nodes.some((node) => node.id === op.id)) return graph; // idempotent retry

        return {
            ...graph,
            nodes: [
                ...graph.nodes,
                {
                    id: op.id,
                    type: NODE_COMPONENT_TYPE,
                    position: { x: op.position?.x ?? 0, y: op.position?.y ?? 0 },
                    data: sanitizeNodeData(op.data || {}),
                },
            ],
        };
    },

    updateNodeData(graph, op) {
        let found = false;
        const nodes = graph.nodes.map((node) => {
            if (node.id !== op.id) return node;
            found = true;
            return { ...node, data: { ...node.data, ...sanitizeNodeData(op.data || {}, node.data) } };
        });

        // The server 404s here, but locally a missing node means the author
        // deleted it before the debounce flushed — nothing to update, and nothing
        // worth throwing an error into a keystroke handler for.
        return found ? { ...graph, nodes } : graph;
    },

    moveNodes(graph, op) {
        const byId = new Map((op.positions || []).map((entry) => [entry.id, entry.position]));
        if (byId.size === 0) return graph;

        return {
            ...graph,
            nodes: graph.nodes.map((node) => {
                const position = byId.get(node.id);
                return position ? { ...node, position: { x: position.x, y: position.y } } : node;
            }),
        };
    },

    /**
     * Removes a node and its attached edges. `rewireIncoming` reproduces the
     * behaviour the old editor had: incoming choices loop back on their own
     * source and become hints rather than being discarded, so the text an author
     * wrote on them survives deleting the situation they pointed at.
     */
    removeNode(graph, op) {
        if (!graph.nodes.some((node) => node.id === op.id)) return graph; // idempotent retry

        const nodes = graph.nodes.filter((node) => node.id !== op.id);
        let edges;

        if (op.rewireIncoming) {
            edges = graph.edges
                .filter((edge) => edge.source !== op.id)
                .map((edge) => (edge.target === op.id ? withEdgeType({ ...edge, target: edge.source }) : edge));
        } else {
            edges = graph.edges.filter((edge) => edge.source !== op.id && edge.target !== op.id);
        }

        return { ...graph, nodes, edges };
    },

    addEdge(graph, op) {
        if (!isId(op.id)) throw new OpError('edge id must be a 24-character hex id.');
        if (graph.edges.some((edge) => edge.id === op.id)) return graph; // idempotent retry

        const edge = withEdgeType({
            id: op.id,
            source: op.source,
            target: op.target,
            type: EDGE_COMPONENT_TYPE,
            data: sanitizeEdgeData(op.data || {}),
        });

        return { ...graph, edges: [...graph.edges, edge] };
    },

    updateEdgeData(graph, op) {
        let found = false;
        const edges = graph.edges.map((edge) => {
            if (edge.id !== op.id) return edge;
            found = true;
            return withEdgeType({
                ...edge,
                data: { ...edge.data, ...sanitizeEdgeData(op.data || {}) },
            });
        });

        return found ? { ...graph, edges } : graph;
    },

    setEdgeTarget(graph, op) {
        let found = false;
        const edges = graph.edges.map((edge) => {
            if (edge.id !== op.id) return edge;
            found = true;
            return withEdgeType({ ...edge, target: op.target });
        });

        return found ? { ...graph, edges } : graph;
    },

    removeEdge(graph, op) {
        const edges = graph.edges.filter((edge) => edge.id !== op.id);
        return edges.length === graph.edges.length ? graph : { ...graph, edges };
    },

    /**
     * Reorders one node's outgoing edges. Choices are presented to a player in
     * the order their edges leave the situation — publish reads
     * `Situation.choices` straight off that order — so dragging a choice up the
     * list is exactly this and nothing else.
     *
     * Edges belonging to other nodes keep their absolute positions, so two
     * authors reordering different situations cannot scramble each other's work.
     */
    reorderEdges(graph, op) {
        const slots = [];
        const current = [];
        graph.edges.forEach((edge, index) => {
            if (edge.source !== op.source) return;
            slots.push(index);
            current.push(edge);
        });
        if (slots.length === 0) return graph;

        const byId = new Map(current.map((edge) => [edge.id, edge]));
        const ordered = [];
        for (const edgeId of op.edgeIds || []) {
            const edge = byId.get(edgeId);
            if (edge && !ordered.includes(edge)) ordered.push(edge);
        }
        for (const edge of current) {
            if (!ordered.includes(edge)) ordered.push(edge);
        }

        const edges = [...graph.edges];
        slots.forEach((slot, index) => {
            edges[slot] = ordered[index];
        });

        return { ...graph, edges };
    },

    addGroup(graph, op) {
        if (!isId(op.id)) throw new OpError('group id must be a 24-character hex id.');
        if (graph.groups.some((group) => group.id === op.id)) return graph; // idempotent retry

        return {
            ...graph,
            groups: [...graph.groups, { id: op.id, ...sanitizeGroupData(op.data || {}) }],
        };
    },

    updateGroup(graph, op) {
        let found = false;
        const groups = graph.groups.map((group) => {
            if (group.id !== op.id) return group;
            found = true;
            return { ...group, ...sanitizeGroupData(op.data || {}) };
        });

        return found ? { ...graph, groups } : graph;
    },

    /**
     * Deletes a group and clears the nodes that belonged to it.
     *
     * The membership sweep is part of the op, not a separate local fix-up, which is
     * what makes it survive a rebase and reach collaborators. Node fillColor is
     * deliberately untouched: group colour is resolved at render, so clearing the
     * fill here would only destroy a colour the author chose for that node.
     */
    removeGroup(graph, op) {
        const groups = graph.groups.filter((group) => group.id !== op.id);
        const nodes = graph.nodes.map((node) =>
            node.data?.situationGroup && String(node.data.situationGroup) === op.id
                ? { ...node, data: { ...node.data, situationGroup: null } }
                : node
        );

        return { ...graph, groups, nodes };
    },

    addKey(graph, op) {
        if (!isId(op.id)) throw new OpError('key id must be a 24-character hex id.');
        if (graph.keys.some((key) => key.id === op.id)) return graph; // idempotent retry

        const data = sanitizeKeyData(op.data || {});
        if (!data.name) throw new OpError('A key needs a name.');

        return {
            ...graph,
            keys: [...graph.keys, { id: op.id, name: data.name, description: data.description ?? '' }],
        };
    },

    updateKey(graph, op) {
        let found = false;
        const keys = graph.keys.map((key) => {
            if (key.id !== op.id) return key;
            found = true;
            return { ...key, ...sanitizeKeyData(op.data || {}) };
        });

        return found ? { ...graph, keys } : graph;
    },

    /**
     * Deletes a key and sweeps every reference to it out of the graph. The
     * published Choice/Situation documents and players' inventories are swept at
     * publish instead — that is when the key actually stops existing for players.
     */
    removeKey(graph, op) {
        const withoutKey = (list) =>
            Array.isArray(list) ? list.filter((keyId) => String(keyId) !== op.id) : list;

        const dropsKey = (data = {}) =>
            (Array.isArray(data.grantsKeys) && data.grantsKeys.some((k) => String(k) === op.id)) ||
            (Array.isArray(data.removesKeys) && data.removesKeys.some((k) => String(k) === op.id));

        return {
            ...graph,
            keys: graph.keys.filter((key) => key.id !== op.id),
            nodes: graph.nodes.map((node) =>
                dropsKey(node.data)
                    ? {
                          ...node,
                          data: {
                              ...node.data,
                              grantsKeys: withoutKey(node.data.grantsKeys),
                              removesKeys: withoutKey(node.data.removesKeys),
                          },
                      }
                    : node
            ),
            edges: graph.edges.map((edge) => {
                const gated = edge.data?.key && String(edge.data.key) === op.id;
                if (!gated && !dropsKey(edge.data)) return edge;

                return {
                    ...edge,
                    data: {
                        ...edge.data,
                        grantsKeys: withoutKey(edge.data?.grantsKeys),
                        removesKeys: withoutKey(edge.data?.removesKeys),
                        // A gate with no key behind it would hide the choice from
                        // everyone, so the gate goes with the key.
                        ...(gated ? { key: null, showIfKey: false, hideIfKey: false } : {}),
                    },
                };
            }),
        };
    },

    setTableOfContents(graph, op) {
        return { ...graph, tableOfContents: op.tableOfContents ?? null };
    },
};

/**
 * Applies ops in order to the draft, returning a new draft. Never mutates its input.
 *
 * All five slices are seeded here, so every applier's `{...graph}` spread carries
 * the ones it does not touch through unchanged.
 *
 * @param {{nodes: Array, edges: Array, groups?: Array, keys?: Array, tableOfContents?: Object|null}} graph
 * @param {Array<Object>} ops
 * @returns {{nodes: Array, edges: Array, groups: Array, keys: Array, tableOfContents: Object|null}}
 */
export function applyOps(graph, ops) {
    let next = {
        nodes: graph?.nodes || [],
        edges: graph?.edges || [],
        groups: graph?.groups || [],
        keys: graph?.keys || [],
        tableOfContents: graph?.tableOfContents ?? null,
    };

    for (const op of ops || []) {
        const applier = appliers[op?.type];
        if (!applier) throw new OpError(`Unknown op type: ${op?.type}`);
        next = applier(next, op);
    }

    return next;
}

export { appliers };
